import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Bell, BellRing, ChevronLeft, ChevronRight, Download, Filter, History, Loader2, Mail, MessageCircle, MessageSquare, RefreshCw, RotateCcw, Search, ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, EmptyState } from "../../components/common";
import { FetchButton, FetchDirtyHint, FetchForm, FetchPrompt, useFetchGate } from "../../components/FetchBar";
import { useTerritoryMasters, splitIds, joinIds } from "../../components/TerritoryFilter";
import { MultiSelect } from "../../components/ui/multi-select";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Skeleton } from "../../components/ui/skeleton";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../../components/ui/alert-dialog";
import { useAuth } from "../../context/AuthContext";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { api, errMsg, fmtDate, downloadBlob } from "../../lib/api";

/**
 * Phase 120 — Notification History register (Admin / Supervisor / Tax Collector).
 * One page for all three portals: the backend decides what the logged-in user may see
 * (admin = everything; supervisor / collector = their territory + assignments; in-app = their own).
 */

const ALL = "all";
const PAGE_SIZES = [25, 50, 100];
const EMPTY_FILTERS = { date_from: "", date_to: "", zone_id: "", ward_id: "", event: "", channel: "", status: "", q: "" };

const CHANNEL_META = {
  sms: { label: "SMS", icon: MessageSquare, cls: "bg-[hsl(var(--info))]/10 text-[hsl(var(--info))] border-[hsl(var(--info))]/20" },
  whatsapp: { label: "WhatsApp", icon: MessageCircle, cls: "bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/20" },
  email: { label: "Email", icon: Mail, cls: "bg-slate-100 text-slate-700 border-slate-200" },
  inapp: { label: "In-app", icon: Bell, cls: "bg-primary/10 text-primary border-primary/20" },
};
const STATUS_CLS = {
  sent: "bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/20",
  delivered: "bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))] border-[hsl(var(--success))]",
  suppressed: "bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/20",
  simulated: "bg-[hsl(var(--info))]/10 text-[hsl(var(--info))] border-[hsl(var(--info))]/20",
  failed: "bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/20",
  skipped: "bg-slate-100 text-slate-700 border-slate-300 border-dashed",   // Phase 226: deliberately not sent (setting OFF)
  unread: "bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/20",
  read: "bg-slate-100 text-slate-600 border-slate-200",
};

export function ChannelBadge({ channel, label }) {
  const m = CHANNEL_META[channel] || { label: label || channel || "-", icon: Bell, cls: "bg-muted text-muted-foreground" };
  const Icon = m.icon;
  return (
    <Badge variant="outline" className={`gap-1 font-medium ${m.cls}`} data-testid={`nh-channel-badge-${channel}`}>
      <Icon className="h-3 w-3" aria-hidden="true" /> {m.label}
    </Badge>
  );
}

export function NotifStatusBadge({ status }) {
  const s = String(status || "").toLowerCase();
  return (
    <Badge variant="outline" className={`font-medium capitalize ${STATUS_CLS[s] || "bg-slate-100 text-slate-600 border-slate-200"}`}
      data-testid={`nh-status-badge-${s}`}>
      {s || "-"}
    </Badge>
  );
}

/** Date + IST clock, stacked. */
function When({ row, testId }) {
  return (
    <div className="whitespace-nowrap" data-testid={testId}>
      <p className="text-sm">{row.date_ist ? fmtDate(row.date_ist) : fmtDate(row.created_at)}</p>
      <p className="text-xs text-muted-foreground tabular-nums">
        {row.time_ist ? row.time_ist.slice(0, 5) : "-"}<span className="ml-1 text-[10px] uppercase tracking-wide">IST</span>
      </p>
    </div>
  );
}

function scopeText(scope) {
  if (!scope) return "";
  if (scope.mode === "all") return "All SMS, WhatsApp and Email messages sent by this municipality, plus your own in-app alerts.";
  if (scope.role === "supervisor") {
    return `Messages for properties in your ${scope.zone_ids?.length || 0} mapped zone${scope.zone_ids?.length === 1 ? "" : "s"} and your team's ${scope.assigned_properties || 0} assigned properties, plus your own in-app alerts.`;
  }
  return `Messages for your ${scope.assigned_properties || 0} assigned properties and your ${scope.ward_ids?.length || 0} mapped ward${scope.ward_ids?.length === 1 ? "" : "s"}, plus your own in-app alerts.`;
}

/** Clickable channel totals for the current filter set. */
function CountChip({ label, value, icon: Icon, active, onClick, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId} aria-pressed={active}
      className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-left transition-colors duration-150 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${active ? "border-primary bg-primary/5" : "bg-card"}`}>
      {Icon && <Icon className={`h-4 w-4 ${active ? "text-primary" : "text-muted-foreground"}`} aria-hidden="true" />}
      <span className="text-lg font-semibold tabular-nums font-heading">{Number(value || 0).toLocaleString("en-IN")}</span>
      <span className="text-xs text-muted-foreground">{label}</span>
    </button>
  );
}

export default function NotificationHistory() {
  const { code } = useParams();
  const { pathname } = useLocation();
  const { auth } = useAuth();
  const isAdmin = auth?.user?.role === "municipal_admin";
  const [resendRow, setResendRow] = useState(null);   // row awaiting confirmation
  const [resending, setResending] = useState(false);
  // the collector portal is a narrow single column on every screen size -> always the card layout there
  const cardsOnly = pathname.includes(`/${code}/collector/`);
  const masters = useTerritoryMasters(code);
  const [options, setOptions] = useState({ events: [], statuses: [], channels: [] });
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);

  // Phase 176: zone_id / ward_id are comma-joined lists ("" = all); wards cascade from the selected zones
  const zoneSel = useMemo(() => splitIds(filters.zone_id), [filters.zone_id]);
  const wardSel = useMemo(() => splitIds(filters.ward_id), [filters.ward_id]);
  const wards = useMemo(() => masters.wards.filter((w) => !zoneSel.length || zoneSel.includes(w.zone_id)), [masters.wards, zoneSel]);
  const zoneNameOf = (id) => masters.zones.find((z) => z.id === id)?.zone_name;
  const changeZones = (ids) => {
    const allowed = new Set(masters.wards.filter((w) => !ids.length || ids.includes(w.zone_id)).map((w) => w.id));
    setF({ zone_id: joinIds(ids), ward_id: joinIds(wardSel.filter((id) => allowed.has(id))) });
  };

  useEffect(() => {
    api.get(`/t/${code}/notification-history/options`).then((r) => setOptions(r.data)).catch(() => {});
  }, [code]);

  // Phase 133: the register loads only on Fetch, with the filters applied at that moment;
  // page / page-size changes fetch immediately on the applied filters
  const gate = useFetchGate(filters);
  const applied = gate.applied || EMPTY_FILTERS;
  useEffect(() => { setPage(1); }, [gate.appliedKey]);

  const params = useMemo(() => {
    const p = { page, limit };
    Object.entries(applied).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [gate.appliedKey, page, limit]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    if (!gate.fetched) return;
    setLoading(true);
    setError("");
    try {
      const r = await api.get(`/t/${code}/notification-history`, { params });
      setData(r.data);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [code, params, gate.fetched, gate.version]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const setF = (patch) => setFilters((f) => ({ ...f, ...patch }));
  // the channel count chips are explicit clicks on the results — they apply AND fetch straight away
  const focusChannel = (patch) => { setFilters((f) => ({ ...f, ...patch })); setTimeout(gate.fetch, 0); };
  const hasFilters = Object.values(filters).some(Boolean);
  const appliedHasFilters = Object.values(applied).some(Boolean);
  const clear = () => { setFilters(EMPTY_FILTERS); setPage(1); gate.reset(); setData(null); };

  const doExport = async () => {
    setExporting(true);
    try {
      const qs = new URLSearchParams(Object.fromEntries(Object.entries(applied).filter(([, v]) => v))).toString();
      await downloadBlob(`/t/${code}/notification-history/export${qs ? `?${qs}` : ""}`,
        `notification-history_${applied.date_from || "all"}_to_${applied.date_to || "today"}.xlsx`);
      toast.success("Excel downloaded", { description: `${(data?.total || 0).toLocaleString("en-IN")} row${data?.total === 1 ? "" : "s"} with the current filters` });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setExporting(false);
    }
  };

  // Phase 123: retry a failed / suppressed message with the current gateway settings (admin only)
  const canResend = (r) => isAdmin && r.kind === "outbox" && ["failed", "suppressed"].includes(r.status) && !r.resent_to_id && !["password_reset_otp", "password_reset"].includes(r.event);
  const doResend = async () => {
    if (!resendRow) return;
    setResending(true);
    try {
      const r = await api.post(`/t/${code}/notification-history/${resendRow.id}/resend`);
      const st = r.data.status;
      const msg = st === "sent" ? "Message resent" : st === "suppressed" ? "Still suppressed" : st === "simulated" ? "Logged only (simulation mode)" : "Resend failed";
      const desc = st === "suppressed" ? "The recipient is not on the live allow-list — ask the Super Admin to add the number or go public."
        : st === "failed" ? (r.data.error || "The gateway rejected the message again.") : `${resendRow.channel_label} to ${resendRow.to}`;
      (st === "sent" ? toast.success : st === "failed" ? toast.error : toast.warning)(msg, { description: desc });
      setResendRow(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setResending(false);
    }
  };

  const total = data?.total || 0;
  const from = total ? (page - 1) * limit + 1 : 0;
  const to = Math.min(page * limit, total);
  const pages = data?.pages || 1;
  const counts = data?.counts || {};
  const msgStatuses = options.statuses.filter((s) => s.group === "Messages");
  const inappStatuses = options.statuses.filter((s) => s.group === "In-app");

  return (
    <div className="space-y-4" data-testid="notification-history-page">
      <PageHeader title="Notification History" subtitle={scopeText(data?.scope) || "Every SMS, WhatsApp, Email and in-app alert — filter, review and export."}>
        <Button variant="outline" className="h-10" onClick={load} disabled={loading || !gate.fetched} data-testid="nh-refresh-button">
          <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
        <Button className="h-10" onClick={doExport} disabled={exporting || loading || !gate.fetched || !total} data-testid="nh-export-button">
          <Download className="h-4 w-4 mr-1.5" /> {exporting ? "Preparing..." : "Export to Excel"}
        </Button>
      </PageHeader>

      {/* channel totals for the current filters — click to focus one channel */}
      {gate.fetched && (
        <div className="flex flex-wrap gap-2" data-testid="nh-counts">
          <CountChip label="Total" value={total} icon={History} active={!applied.channel} onClick={() => focusChannel({ channel: "", status: "" })} testId="nh-count-total" />
          <CountChip label="SMS" value={counts.sms} icon={MessageSquare} active={applied.channel === "sms"} onClick={() => focusChannel({ channel: "sms" })} testId="nh-count-sms" />
          <CountChip label="WhatsApp" value={counts.whatsapp} icon={MessageCircle} active={applied.channel === "whatsapp"} onClick={() => focusChannel({ channel: "whatsapp" })} testId="nh-count-whatsapp" />
          <CountChip label="Email" value={counts.email} icon={Mail} active={applied.channel === "email"} onClick={() => focusChannel({ channel: "email" })} testId="nh-count-email" />
          <CountChip label="In-app" value={counts.inapp} icon={BellRing} active={applied.channel === "inapp"} onClick={() => focusChannel({ channel: "inapp" })} testId="nh-count-inapp" />
        </div>
      )}

      {/* filter bar */}
      <Card className="shadow-sm">
        <FetchForm onFetch={gate.fetch} testId="nh-filter-form">
        <CardContent className="p-4 flex flex-wrap gap-3 items-end" data-testid="nh-filter-bar">
          <div className="space-y-1">
            <Label className="text-xs">Date From</Label>
            <Input type="date" className="w-36 h-10" value={filters.date_from} max={filters.date_to || undefined}
              onChange={(e) => setF({ date_from: e.target.value })} data-testid="nh-filter-from" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Date To</Label>
            <Input type="date" className="w-36 h-10" value={filters.date_to} min={filters.date_from || undefined}
              onChange={(e) => setF({ date_to: e.target.value })} data-testid="nh-filter-to" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Zone{zoneSel.length > 1 ? ` (${zoneSel.length})` : ""}</Label>
            <MultiSelect options={masters.zones.map((z) => ({ value: z.id, label: z.zone_name }))} value={zoneSel} onChange={changeZones}
              allLabel="All zones" noun="zone" placeholder="Search zones…" className="w-44 h-10" testId="nh-filter-zone" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Ward{wardSel.length > 1 ? ` (${wardSel.length})` : ""}</Label>
            <MultiSelect options={wards.map((w) => ({ value: w.id, label: w.ward_name, group: zoneSel.length === 1 ? undefined : zoneNameOf(w.zone_id) }))}
              value={wardSel} onChange={(ids) => setF({ ward_id: joinIds(ids) })}
              allLabel="All wards" noun="ward" placeholder="Search wards…" className="w-44 h-10" testId="nh-filter-ward" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Event</Label>
            <Select value={filters.event || ALL} onValueChange={(v) => setF({ event: v === ALL ? "" : v })}>
              <SelectTrigger className="w-48 h-10" data-testid="nh-filter-event"><SelectValue placeholder="All events" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value={ALL}>All events</SelectItem>
                {options.events.map((e) => <SelectItem key={e.key} value={e.key} data-testid={`nh-event-option-${e.key}`}>{e.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Channel</Label>
            <Select value={filters.channel || ALL} onValueChange={(v) => setF({ channel: v === ALL ? "" : v })}>
              <SelectTrigger className="w-36 h-10" data-testid="nh-filter-channel"><SelectValue placeholder="All channels" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All channels</SelectItem>
                {Object.entries(CHANNEL_META).map(([k, m]) => <SelectItem key={k} value={k} data-testid={`nh-channel-option-${k}`}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Status</Label>
            <Select value={filters.status || ALL} onValueChange={(v) => setF({ status: v === ALL ? "" : v })}>
              <SelectTrigger className="w-36 h-10" data-testid="nh-filter-status"><SelectValue placeholder="All statuses" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All statuses</SelectItem>
                <SelectGroup>
                  <SelectLabel>Messages</SelectLabel>
                  {msgStatuses.map((s) => <SelectItem key={s.key} value={s.key} data-testid={`nh-status-option-${s.key}`}>{s.label}</SelectItem>)}
                </SelectGroup>
                <SelectGroup>
                  <SelectLabel>In-app</SelectLabel>
                  {inappStatuses.map((s) => <SelectItem key={s.key} value={s.key} data-testid={`nh-status-option-${s.key}`}>{s.label}</SelectItem>)}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1 flex-1 min-w-[200px]">
            <Label className="text-xs">PIN or recipient</Label>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-2.5 top-3 text-muted-foreground" aria-hidden="true" />
              <Input className="h-10 pl-8" placeholder="Search PIN, mobile or email..." value={filters.q}
                onChange={(e) => setF({ q: e.target.value })} data-testid="nh-filter-search" />
            </div>
          </div>
          <FetchButton type="submit" loading={loading} dirty={gate.dirty} fetched={gate.fetched} testId="nh-fetch-button" />
          {hasFilters && (
            <Button type="button" variant="ghost" size="sm" className="h-10 text-muted-foreground" onClick={clear} data-testid="nh-filter-clear">
              <X className="h-4 w-4 mr-1" /> Clear filters
            </Button>
          )}
          <FetchDirtyHint dirty={gate.dirty} testId="nh-fetch-dirty" className="basis-full" />
        </CardContent>
        </FetchForm>
      </Card>

      {/* results */}
      {!gate.fetched ? (
        <Card className="shadow-sm"><CardContent className="p-0">
          <FetchPrompt onFetch={gate.fetch} testId="nh-fetch-prompt"
            subtitle="Pick the date range, area, event, channel or status above, then click Fetch to load the notification register." />
        </CardContent></Card>
      ) : error ? (
        <Card className="shadow-sm"><CardContent className="p-8 text-center space-y-3" data-testid="nh-error">
          <p className="text-sm text-destructive">{error}</p>
          <Button variant="outline" onClick={load} data-testid="nh-retry-button"><RefreshCw className="h-4 w-4 mr-1.5" /> Retry</Button>
        </CardContent></Card>
      ) : loading && !data ? (
        <Card className="shadow-sm"><CardContent className="p-4 space-y-2" data-testid="nh-loading">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
        </CardContent></Card>
      ) : total === 0 ? (
        <EmptyState title="No notifications found"
          subtitle={appliedHasFilters ? "Try widening the date range or clearing filters, then Fetch again" : "Messages will appear here as demands, payments and alerts are sent"}
          action={appliedHasFilters ? <Button variant="outline" onClick={clear} data-testid="nh-empty-clear">Clear filters</Button> : null} />
      ) : (
        <Card className="shadow-sm">
          {/* desktop / tablet table */}
          <div className={`${cardsOnly ? "hidden" : "hidden md:block"} overflow-x-auto ${loading ? "opacity-60" : ""}`} aria-busy={loading}>
            <Table data-testid="nh-table">
              <TableHeader className="sticky top-0 bg-card z-10">
                <TableRow>
                  <TableHead className="w-28">Date / Time</TableHead>
                  <TableHead className="w-40">Event</TableHead>
                  <TableHead className="w-28">Channel</TableHead>
                  <TableHead className="w-40">Recipient</TableHead>
                  <TableHead className="w-36">PIN</TableHead>
                  <TableHead className="w-24">Status</TableHead>
                  <TableHead>Message</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((r) => (
                  <TableRow key={`${r.kind}-${r.id}`} className="[&>td]:align-top" data-testid={`nh-row-${r.id}`}>
                    <TableCell><When row={r} testId={`nh-when-${r.id}`} /></TableCell>
                    <TableCell className="text-sm" data-testid={`nh-cell-event-${r.id}`}>{r.event_label}</TableCell>
                    <TableCell><ChannelBadge channel={r.channel} label={r.channel_label} /></TableCell>
                    <TableCell className="text-sm break-all" data-testid={`nh-cell-recipient-${r.id}`}>{r.to || <span className="text-muted-foreground">-</span>}</TableCell>
                    <TableCell data-testid={`nh-cell-pin-${r.id}`}>
                      {r.pin ? <span className="font-mono text-sm">{r.pin}</span> : <span className="text-muted-foreground text-sm">-</span>}
                      {(r.zone_name || r.ward_name) && (
                        <p className="text-xs text-muted-foreground mt-0.5">{[r.zone_name, r.ward_name].filter(Boolean).join(" · ")}</p>
                      )}
                    </TableCell>
                    <TableCell>
                      <NotifStatusBadge status={r.status} />
                      {canResend(r) && (
                        <Button variant="outline" size="sm" className="h-7 mt-1.5 text-xs px-2" onClick={() => setResendRow(r)} data-testid={`nh-resend-${r.id}`}>
                          <RotateCcw className="h-3 w-3 mr-1" /> Resend
                        </Button>
                      )}
                      {r.resent_to_id && (
                        <p className="text-[10px] text-muted-foreground mt-1" data-testid={`nh-resent-${r.id}`}>Resent{r.resent_at ? ` · ${fmtDate(r.resent_at)}` : ""}{r.resent_by_name ? ` by ${r.resent_by_name}` : ""}</p>
                      )}
                      {r.resend_of && (
                        <p className="text-[10px] text-muted-foreground mt-1" data-testid={`nh-resend-of-${r.id}`}>Retry #{r.resend_attempt || 1}</p>
                      )}
                      {r.provider_status && r.provider_status !== r.status && (
                        <p className="text-[10px] text-muted-foreground mt-1 capitalize" data-testid={`nh-cell-provider-status-${r.id}`}>{String(r.provider || "").startsWith("mobicomm") ? "mobiCOMM" : "Twilio"}: {r.provider_status}</p>
                      )}
                      {r.error && <p className={`text-[11px] mt-1 max-w-[10rem] break-words ${r.status === "skipped" ? "text-muted-foreground" : "text-destructive"}`} data-testid={`nh-cell-error-${r.id}`}>{r.error}</p>}
                    </TableCell>
                    <TableCell className="text-xs leading-relaxed whitespace-pre-wrap break-words max-w-xl" data-testid={`nh-cell-message-${r.id}`}>
                      {r.kind === "outbox" && r.subject && <p className="font-medium text-foreground mb-0.5">{r.subject}</p>}
                      {r.message}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* mobile cards */}
          <div className={`${cardsOnly ? "" : "md:hidden"} divide-y ${loading ? "opacity-60" : ""}`} data-testid="nh-cards">
            {data.items.map((r) => (
              <div key={`${r.kind}-${r.id}`} className="p-3 space-y-2" data-testid={`nh-card-${r.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{r.event_label}</p>
                    <p className="text-xs text-muted-foreground">
                      {r.date_ist ? fmtDate(r.date_ist) : fmtDate(r.created_at)} · {r.time_ist ? r.time_ist.slice(0, 5) : "-"} IST
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <ChannelBadge channel={r.channel} label={r.channel_label} />
                    <NotifStatusBadge status={r.status} />
                    {canResend(r) && (
                      <Button variant="outline" size="sm" className="h-7 text-xs px-2" onClick={() => setResendRow(r)} data-testid={`nh-resend-card-${r.id}`}>
                        <RotateCcw className="h-3 w-3 mr-1" /> Resend
                      </Button>
                    )}
                    {r.resent_to_id && <span className="text-[10px] text-muted-foreground">Resent</span>}
                  </div>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs">
                  <span><span className="text-muted-foreground">To:</span> <span className="break-all">{r.to || "-"}</span></span>
                  {r.pin && <span><span className="text-muted-foreground">PIN:</span> <span className="font-mono">{r.pin}</span></span>}
                  {(r.zone_name || r.ward_name) && <span className="text-muted-foreground">{[r.zone_name, r.ward_name].filter(Boolean).join(" · ")}</span>}
                </div>
                <p className="text-xs leading-relaxed whitespace-pre-wrap break-words">
                  {r.kind === "outbox" && r.subject && <span className="font-medium block mb-0.5">{r.subject}</span>}
                  {r.message}
                </p>
                {r.error && <p className={`text-[11px] ${r.status === "skipped" ? "text-muted-foreground" : "text-destructive"}`}>{r.error}</p>}
              </div>
            ))}
          </div>

          {/* pagination */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-t px-4 py-3" data-testid="nh-pagination">
            <p className="text-xs text-muted-foreground" data-testid="nh-showing">
              Showing <span className="font-medium text-foreground tabular-nums">{from.toLocaleString("en-IN")}–{to.toLocaleString("en-IN")}</span> of{" "}
              <span className="font-medium text-foreground tabular-nums">{total.toLocaleString("en-IN")}</span>
            </p>
            <div className="flex items-center gap-2">
              <Select value={String(limit)} onValueChange={(v) => { setLimit(Number(v)); setPage(1); }}>
                <SelectTrigger className="h-9 w-28" data-testid="nh-page-size"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAGE_SIZES.map((n) => <SelectItem key={n} value={String(n)}>{n} / page</SelectItem>)}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" className="h-9" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="nh-prev-page">
                <ChevronLeft className="h-4 w-4" /> Prev
              </Button>
              <span className="text-xs text-muted-foreground tabular-nums" data-testid="nh-page-indicator">Page {page} of {pages.toLocaleString("en-IN")}</span>
              <Button variant="outline" size="sm" className="h-9" disabled={page >= pages || loading} onClick={() => setPage((p) => Math.min(pages, p + 1))} data-testid="nh-next-page">
                Next <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </Card>
      )}

      <AlertDialog open={!!resendRow} onOpenChange={(o) => { if (!o && !resending) setResendRow(null); }}>
        <AlertDialogContent data-testid="nh-resend-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Resend this {resendRow?.channel_label || "message"}?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  <span className="font-medium text-foreground">{resendRow?.event_label}</span> to <span className="font-mono text-foreground">{resendRow?.to}</span>
                  {resendRow?.pin ? <> · PIN <span className="font-mono text-foreground">{resendRow.pin}</span></> : null}
                </p>
                <p>Current status: <span className="capitalize font-medium text-foreground">{resendRow?.status}</span>{resendRow?.error ? ` — ${resendRow.error}` : ""}</p>
                <p className="text-xs">The same text is sent again through the live gateway with today's settings. A new history row is created and this one is marked as resent. Real messages are charged by the gateway; recipients outside the live allow-list stay suppressed.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={resending} data-testid="nh-resend-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); doResend(); }} disabled={resending} data-testid="nh-resend-confirm">
              {resending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-1.5" />} Resend now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <p className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="nh-footnote">
        <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
        Times are shown in IST. "Sent" = accepted by the gateway, "Delivered" = confirmed on the handset, "Simulated" = logged only (simulation mode), "Suppressed" = held back by the live allow-list, "Skipped" = deliberately not sent (e.g. automatic SMS on new demand is switched off).
        <Filter className="h-3.5 w-3.5 ml-2" aria-hidden="true" /> Export downloads exactly the rows matching the active filters.
      </p>
    </div>
  );
}
